import { useMemo, useState } from "react";
import { connect } from "react-redux";

import { Grid, lighten, Typography, useTheme } from "@mui/material";

import * as BotpressSessionActions from "../../state/redux/botpressSession.js";
import { getDateRangeTitleText, OmniStringToDate } from "../../utils/helpers.js";
import BotpressBotUsageLineChart from "../ui/botpressUsageLineChart.js";
import OmniDateRangePopover from "../ui/omniDateRangePopover.js";
import OmniMetricBox from "../ui/omniMetricBox.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressBotUseage = props => {
  const {
    analytics,
    analyticsStartDate,
    analyticsEndDate,
    widgetSize = "medium",
    minHeight = "333px",
  } = props;

  const theme = useTheme();

  const filters = useMemo(
    // Current Analytics object:
    //   {
    //   "startDateTimeUtc": "2025-10-01T10:00:00.000Z",
    //   "endDateTimeUtc": "2025-10-01T22:00:00.000Z",
    //   "returningUsers": 3,
    //   "newUsers": 0,
    //   "sessions": 26,
    //   "userMessages": 79,
    //   "botMessages": 27,
    //   "events": 38,
    //   "eventTypes": {
    //     "plus/livechat-hitl:hitlStopped": 9,
    //     "plus/livechat-hitl:hitlAssigned": 8,
    //     "state_expired": 13,
    //     "webchat:conversationStarted": 4,
    //     "websiteCrawl": 4
    //   },
    //   "customEvents": {},
    //   "llm": {
    //     "calls": 106,
    //     "errors": 0,
    //     "inputTokens": 821374,
    //     "outputTokens": 12563,
    //     "latency": {
    //       "mean": 2303.811320754717,
    //       "sd": 2104.9519035935355,
    //       "min": 452,
    //       "max": 11887
    //     },
    //     "tokensPerSecond": {
    //       "mean": 48.59852121589698,
    //       "sd": 28.095880447583752,
    //       "min": 4.844961240310077,
    //       "max": 143.56743318831573
    //     },
    //     "cost": {
    //       "sum": 0.28497180000000005,
    //       "mean": 0.002688413207547171,
    //       "sd": 0.006498864272325559,
    //       "min": 0.00003540000000000001,
    //       "max": 0.032111200000000006
    //     }
    //   },
    //   "messages": 79
    // }
    () => [
      { metric: "newUsers", label: "New Users", color: "#2F80ED" },
      { metric: "returningUsers", label: "Returning Users", color: "#27AE60" },
      { metric: "messages", label: "Total Messages", color: "#F2994A" },
      { metric: "sessions", label: "Sessions", color: "#9B51E0" },
      { metric: "eventTypes.plus/livechat-hitl:hitlAssigned", label: "HITL", color: "#EB5757" },
    ],
    [],
  );

  const [metricVisibility, setMetricVisibility] = useState(
    Object.fromEntries(filters.map(f => [f.metric, true])),
  );

  const toggleMetricVisibility = metric => {
    setMetricVisibility(prev => ({ ...prev, [metric]: !prev[metric] }));
  };

  const analyticsInfo = useMemo(() => {
    if (!analytics?.records?.length) return [];

    const getNestedValue = (obj, path) =>
      path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : 0), obj);

    return filters.map(myFilter => ({
      ...myFilter,
      value: analytics.records.reduce(
        (sum, record) => sum + getNestedValue(record, myFilter.metric),
        0,
      ),
      visible: metricVisibility[myFilter.metric] ?? true,
    }));
  }, [analytics, filters, metricVisibility]);

  const records = useMemo(() => {
    if (!analytics?.records) return [];
    return analytics.records.map(record => {
      const newRecord = {};
      filters.forEach(myFilter => {
        if (myFilter.metric.includes(".")) {
          const [parent, child] = myFilter.metric.split(".");
          newRecord[parent] = newRecord[parent] || {};
          newRecord[parent][child] = record?.[parent]?.[child] ?? 0;
        } else {
          newRecord[myFilter.metric] = record[myFilter.metric] ?? 0;
        }
      });
      newRecord.startDateTimeUtc = record.startDateTimeUtc;
      newRecord.endDateTimeUtc = record.endDateTimeUtc;
      return newRecord;
    });
  }, [analytics, filters]);

  const titleText = useMemo(() => {
    if (analyticsStartDate && analyticsEndDate) {
      return getDateRangeTitleText(analyticsStartDate, analyticsEndDate);
    }
    return "Loading...";
  }, [analyticsStartDate, analyticsEndDate]);

  const onRangeChange = (newStartDate, newEndDate) => {
    props.setAnalyticsDatesRequest({
      botODM: null,
      analyticsStartDate: new Date(OmniStringToDate(newStartDate)),
      analyticsEndDate: new Date(OmniStringToDate(newEndDate)),
    });
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle title={"Analytics"} text={titleText}>
        <OmniDateRangePopover
          analyticsStartDate={analyticsStartDate}
          analyticsEndDate={analyticsEndDate}
          onRangeChange={onRangeChange}
        />
      </WidgetTitle>

      {!analyticsInfo.length && <WidgetSkeleton />}
      {!!analyticsInfo.length && (
        <WidgetContent>
          <Grid container spacing={0} height={"100%"}>
            <Grid
              size={{ xs: 12, sm: 6, md: 3 }}
              justifyContent={"flex-start"}
              alignContent={"flex-start"}
              paddingLeft={1}
              paddingRight={1}
            >
              <Typography variant="overline">Graph Filters</Typography>
              {analyticsInfo.map((info, i) => (
                <OmniMetricBox
                  key={info.metric}
                  label={info.label}
                  value={info.value}
                  bgColor={i % 2 === 0 ? info.color : lighten(info.color, 0.3)}
                  color={theme.palette.secondary.contrastText}
                  active={info.visible}
                  onClick={() => toggleMetricVisibility(info.metric)}
                />
              ))}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 9 }} height={"100%"}>
              <BotpressBotUsageLineChart
                records={records}
                filters={filters}
                metricVisibility={metricVisibility}
              />
            </Grid>
          </Grid>
        </WidgetContent>
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  analytics: state.botpressSession.analytics,
  analyticsStartDate: state.botpressSession.analyticsStartDate,
  analyticsEndDate: state.botpressSession.analyticsEndDate,
});

const mapDispatchToProps = {
  setAnalyticsDatesRequest: BotpressSessionActions.setAnalyticsDatesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressBotUseage);
